<script setup lang="ts">
import { inject, nextTick, onMounted, provide, reactive, ref, watchEffect } from 'vue';
import useBackend from './composition/useBackend';
import { FloatingPanel } from '@hantera/design-system';
import { phraseContextKey } from './keys';
import { Dialog } from './dialog';
import DialogContext from './DialogContext.vue';
import AppsContext from './AppsContext.vue';
import StateContext from './StateContext.vue';
import Workspace from "./view-managers/workspace";
import Stack from "./view-managers/stack";
import { GraphQueryNavigation, legacyModeKey, QueryExportFormat, useContext } from "@hantera/portal-app";
import QueryExport from './views/QueryView/QueryExport.vue';

const backend = useBackend();
const legacyMode = inject(legacyModeKey, false);
const context = useContext()

onMounted(async () => {
  if (legacyMode) {
    await backend.legacyTagDefinitions.refresh();
    await backend.legacyChannels.refresh();
  }
  await backend.phrases.refresh();
});

let dialogId = 0;
const dialogs: Dialog[] = reactive([]);

const useAddDialog = () => {
  const phraseContext = inject<string>(phraseContextKey, '');

  return function (
    label: string,
    component: any,
    showClose: boolean = false,
    props?: object,
    destroyOnClose = false,
    style?: {
      defaultWidth?: string
    },
    onClose?: () => void
  ) {
    function destroy() {
      dialogs.splice(dialogs.indexOf(dialog), 1);
      dialog.elem = undefined;
    }

    const dialog: Dialog = {
      id: dialogId++,
      label,
      showClose,
      component,
      props,
      phraseContext: phraseContext,
      style,
      controller: {
        open() {
          nextTick(() => {
            dialog.elem?.open();
          });
        },
        openModal() {
          nextTick(() => {
            dialog.elem?.openModal();
          });
        },
        close() {
          dialog.elem?.close();
        },
        destroy,
        onClose() {
          if (onClose !== undefined) {
            onClose();
          }
          if (destroyOnClose) {
            destroy();
          }
        }
      }
    }

    dialogs.push(dialog);
    return dialog.controller;
  }
}

provide('$portal_addDialog', useAddDialog);

provide('$portal_queryExporter', () => {

  const addDialog = useAddDialog()

  return (query: GraphQueryNavigation, format: QueryExportFormat = 'csv', name?: string) => {

    return new Promise<void>((res) => {
      const controller = addDialog(
        'Exporting',
        QueryExport,
        false,
        {
          queryName: name ?? 'Export',
          query: query,
          format
        },
        true,
        undefined,
        () => {
          res()
        }
      );
      controller.openModal();
    })
  }

})

const langFormatKey = ref('')

watchEffect(() => {

  const langauge = backend.user.value.settings.language ?? navigator.language
  const formatting = backend.user.value.settings.formatting ?? navigator.language;

  document.documentElement.lang = langauge
  document.documentElement.dataset['formatting'] = formatting

  langFormatKey.value = `${langauge}::${formatting}`

})

</script>

<template>
  <Suspense>
    <StateContext>
      <Suspense suspensible>
        <AppsContext :key="langFormatKey">

          <Workspace v-if="context.mode === 'workspace'"/>
          <Stack v-else />
          
          <FloatingPanel
            v-for="dialog in dialogs"
            :key="dialog.id"
            :ref="(el: any) => dialog.elem = el"
            :label="dialog.label"
            :show-close="dialog.showClose"
            :default-width="dialog.style?.defaultWidth"
            @close="dialog.controller.onClose"
          >
            <DialogContext :dialog="dialog" />
          </FloatingPanel>
        </AppsContext>
        <template #fallback>
          <div class="loading">
            <div class="loading-logo">
              <svg
                viewBox="0 0 127 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M8.90095 7.71353C6.48156 7.71353 4.54008 8.87842 3.10637 10.4017V0.724197H0V23.1259H3.10637V15.2405C3.25572 13.0003 4.74916 10.6706 7.70619 10.6706C9.88663 10.6706 11.3502 11.9848 11.3502 15.6288V23.1259H14.4566V14.2548C14.4566 10.0433 12.4554 7.71353 8.90095 7.71353Z"
                  fill="currentColor"
                />
                <path
                  d="M17.4862 15.5691C17.4862 19.9001 20.7419 23.4246 25.0729 23.4246C27.791 23.4246 29.9415 21.9312 30.8077 19.9897L30.9869 23.1259H33.944V8.01222H30.9869L30.8077 11.1186C29.9415 9.20698 27.791 7.71353 25.0729 7.71353C20.7419 7.71353 17.4862 11.2381 17.4862 15.5691ZM20.5925 15.5691C20.5925 12.8809 22.7132 10.6706 25.5807 10.6706C28.3883 10.6706 30.4792 12.8809 30.4792 15.5691C30.4792 18.2573 28.3883 20.4676 25.5807 20.4676C22.7132 20.4676 20.5925 18.2573 20.5925 15.5691Z"
                  fill="currentColor"
                />
                <path
                  d="M46.8777 7.71353C44.4284 7.71353 42.487 8.90829 41.0533 10.4316L40.9039 8.01222H37.9767V23.1259H41.0831V15.2405C41.2325 13.0003 42.7259 10.6706 45.6829 10.6706C47.8634 10.6706 49.327 11.9848 49.327 15.6288V23.1259H52.4333V14.2548C52.4333 10.0433 50.4321 7.71353 46.8777 7.71353Z"
                  fill="currentColor"
                />
                <path
                  d="M64.0442 8.01222H60.8183V3.53188H57.6821V8.01222H55.4419V10.9693H57.6821V23.1259H60.8183V10.9693H64.0442V8.01222Z"
                  fill="currentColor"
                />
                <path
                  d="M81.1498 15.7483C81.1498 11.4472 78.2525 7.71353 73.5929 7.71353C69.0528 7.71353 66.0361 11.2679 66.0361 15.4795C66.0361 19.9897 69.232 23.4246 73.8319 23.4246C77.8941 23.4246 80.2537 20.6767 80.7615 18.5858L78.0434 18.2871C77.4161 19.452 76.0422 20.6468 73.8617 20.6468C70.9645 20.6468 69.232 18.8547 69.1424 16.5249H81.1498V15.7483ZM73.5929 10.4914C76.2214 10.4914 77.894 12.3134 78.0135 14.3444H69.1424C69.2619 12.2238 71.0541 10.4914 73.5929 10.4914Z"
                  fill="currentColor"
                />
                <path
                  d="M90.9372 7.71353C89.623 7.71353 88.488 8.54986 87.5322 9.77449L87.3828 8.01222H84.4557V23.1259H87.562V15.9872C87.562 12.9705 88.6074 10.6706 90.758 10.6706C91.4151 10.6706 92.0125 10.9095 92.3112 11.0887L92.7891 8.37065C92.4605 8.07196 91.8333 7.71353 90.9372 7.71353Z"
                  fill="currentColor"
                />
                <path
                  d="M94.2121 15.5691C94.2121 19.9001 97.4678 23.4246 101.799 23.4246C104.517 23.4246 106.667 21.9312 107.534 19.9897L107.713 23.1259H110.67V8.01222H107.713L107.534 11.1186C106.667 9.20698 104.517 7.71353 101.799 7.71353C97.4678 7.71353 94.2121 11.2381 94.2121 15.5691ZM97.3184 15.5691C97.3184 12.8809 99.4391 10.6706 102.307 10.6706C105.114 10.6706 107.205 12.8809 107.205 15.5691C107.205 18.2573 105.114 20.4676 102.307 20.4676C99.4391 20.4676 97.3184 18.2573 97.3184 15.5691Z"
                  fill="currentColor"
                />
                <path
                  d="M114.049 23.135H126.001V25.2758H114.049V23.135Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div class="loading-message">
              Loading apps...
            </div>
          </div>
        </template>
      </Suspense>
    </StateContext>
    <template #fallback>
      <div class="loading">
        <div class="loading-logo">
          <svg
            viewBox="0 0 127 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.90095 7.71353C6.48156 7.71353 4.54008 8.87842 3.10637 10.4017V0.724197H0V23.1259H3.10637V15.2405C3.25572 13.0003 4.74916 10.6706 7.70619 10.6706C9.88663 10.6706 11.3502 11.9848 11.3502 15.6288V23.1259H14.4566V14.2548C14.4566 10.0433 12.4554 7.71353 8.90095 7.71353Z"
              fill="currentColor"
            />
            <path
              d="M17.4862 15.5691C17.4862 19.9001 20.7419 23.4246 25.0729 23.4246C27.791 23.4246 29.9415 21.9312 30.8077 19.9897L30.9869 23.1259H33.944V8.01222H30.9869L30.8077 11.1186C29.9415 9.20698 27.791 7.71353 25.0729 7.71353C20.7419 7.71353 17.4862 11.2381 17.4862 15.5691ZM20.5925 15.5691C20.5925 12.8809 22.7132 10.6706 25.5807 10.6706C28.3883 10.6706 30.4792 12.8809 30.4792 15.5691C30.4792 18.2573 28.3883 20.4676 25.5807 20.4676C22.7132 20.4676 20.5925 18.2573 20.5925 15.5691Z"
              fill="currentColor"
            />
            <path
              d="M46.8777 7.71353C44.4284 7.71353 42.487 8.90829 41.0533 10.4316L40.9039 8.01222H37.9767V23.1259H41.0831V15.2405C41.2325 13.0003 42.7259 10.6706 45.6829 10.6706C47.8634 10.6706 49.327 11.9848 49.327 15.6288V23.1259H52.4333V14.2548C52.4333 10.0433 50.4321 7.71353 46.8777 7.71353Z"
              fill="currentColor"
            />
            <path
              d="M64.0442 8.01222H60.8183V3.53188H57.6821V8.01222H55.4419V10.9693H57.6821V23.1259H60.8183V10.9693H64.0442V8.01222Z"
              fill="currentColor"
            />
            <path
              d="M81.1498 15.7483C81.1498 11.4472 78.2525 7.71353 73.5929 7.71353C69.0528 7.71353 66.0361 11.2679 66.0361 15.4795C66.0361 19.9897 69.232 23.4246 73.8319 23.4246C77.8941 23.4246 80.2537 20.6767 80.7615 18.5858L78.0434 18.2871C77.4161 19.452 76.0422 20.6468 73.8617 20.6468C70.9645 20.6468 69.232 18.8547 69.1424 16.5249H81.1498V15.7483ZM73.5929 10.4914C76.2214 10.4914 77.894 12.3134 78.0135 14.3444H69.1424C69.2619 12.2238 71.0541 10.4914 73.5929 10.4914Z"
              fill="currentColor"
            />
            <path
              d="M90.9372 7.71353C89.623 7.71353 88.488 8.54986 87.5322 9.77449L87.3828 8.01222H84.4557V23.1259H87.562V15.9872C87.562 12.9705 88.6074 10.6706 90.758 10.6706C91.4151 10.6706 92.0125 10.9095 92.3112 11.0887L92.7891 8.37065C92.4605 8.07196 91.8333 7.71353 90.9372 7.71353Z"
              fill="currentColor"
            />
            <path
              d="M94.2121 15.5691C94.2121 19.9001 97.4678 23.4246 101.799 23.4246C104.517 23.4246 106.667 21.9312 107.534 19.9897L107.713 23.1259H110.67V8.01222H107.713L107.534 11.1186C106.667 9.20698 104.517 7.71353 101.799 7.71353C97.4678 7.71353 94.2121 11.2381 94.2121 15.5691ZM97.3184 15.5691C97.3184 12.8809 99.4391 10.6706 102.307 10.6706C105.114 10.6706 107.205 12.8809 107.205 15.5691C107.205 18.2573 105.114 20.4676 102.307 20.4676C99.4391 20.4676 97.3184 18.2573 97.3184 15.5691Z"
              fill="currentColor"
            />
            <path
              d="M114.049 23.135H126.001V25.2758H114.049V23.135Z"
              fill="currentColor"
            />
          </svg>
        </div>
        <div class="loading-message">
          Loading configuration...
        </div>
      </div>
    </template>
  </Suspense>
</template>

<style>
html,
body,
#app {
  height: 100%;
  overflow: hidden;
  background-color: hsl(var(--color-100));
}
</style>

<style scoped>
.loading {
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  gap: 10px;
}

.loading-logo {
  width: 100%;
  max-width: 300px;
}
</style>
